import { DateTime } from 'luxon'
import * as zod from 'zod'
import { DeviceBufferSchema, type AbstractDepartmentPageLine } from '../Device'
import type { AtLeast, NoArgsConstructor } from '../Utils'
import type { PartialDeep } from 'type-fest'

export interface WidgetElementData extends AbstractDepartmentPageLine {
  id: string
  order: number
  data: number | null
}
export interface GraphWidgetElementData extends WidgetElementData {
  color: string
}
export interface AbstractWidgetData<_T extends WidgetElementData = WidgetElementData> {
  type: WidgetType
}
export interface BaseWidgetElementDialogProps {
  type: WidgetType
  validationPath?: string
  // element: WidgetElement
}
export interface WidgetBufferValue {
  value: number | null
  timestamp: string
}

export const MULTIGRID_CHART = zod.literal('MULTIGRID_CHART')
export const LINE_CHART = zod.literal('LINE_CHART')
export const MULTIPLE_Y_CHART = zod.literal('MULTIPLE_Y_CHART')
export const PAGE = zod.literal('PAGE')
export const LINE = zod.literal('LINE')
export const TABLE = zod.literal('TABLE')
export const BAR_CHART = zod.literal('BAR_CHART')
export const PICTOGRAPH = zod.literal('PICTOGRAPH')
export const CARD_CHART = zod.literal('CARD_CHART')
export const COLLAPSABLE_TABLE = zod.literal('COLLAPSABLE_TABLE')
export const TIME_SELECTION = zod.literal('TIME_SELECTION')

const WidgetTypeSchema = zod.union([
  MULTIGRID_CHART, LINE_CHART, MULTIPLE_Y_CHART, PAGE,
  LINE, TABLE, BAR_CHART, PICTOGRAPH,
  CARD_CHART, COLLAPSABLE_TABLE, TIME_SELECTION,
])
export type WidgetType = zod.infer<typeof WidgetTypeSchema>
export const widgetTypeValues = WidgetTypeSchema.options.map(o => o.value)

const WidgetPositionSchema = zod.object({
  i: zod.string(),
  x: zod.number(),
  y: zod.number(),
  w: zod.number(),
  h: zod.number(),
})
export type WidgetPosition = zod.infer<typeof WidgetPositionSchema>

const Last24HoursIntervalTypeSchema = zod.literal('LAST_24_HOURS')
const Last7DaysIntervalTypeSchema = zod.literal('LAST_7_DAYS')
const Last30DaysIntervalTypeSchema = zod.literal('LAST_30_DAYS')
const ThisMonthIntervalTypeSchema = zod.literal('THIS_MONTH')
const LastMonthIntervalTypeSchema = zod.literal('LAST_MONTH')
const CustomIntervalTypeSchema = zod.literal('INTERVAL')

export const WidgetIntervalTypeSchema = zod.union([
  Last24HoursIntervalTypeSchema,
  Last7DaysIntervalTypeSchema,
  Last30DaysIntervalTypeSchema,
  ThisMonthIntervalTypeSchema,
  LastMonthIntervalTypeSchema,
  CustomIntervalTypeSchema,
])
export type WidgetIntervalType = zod.infer<typeof WidgetIntervalTypeSchema>

const BaseWidgetInterval = zod.object({
  start: zod.string().superRefine(DateTimeValidator),
  end: zod.string().superRefine(DateTimeValidator),
  type: WidgetIntervalTypeSchema.default('LAST_24_HOURS'),
})
const Last24HoursIntervalSchema = zod.object({
  type: Last24HoursIntervalTypeSchema,
})
const Last7DaysIntervalSchema = zod.object({
  type: Last7DaysIntervalTypeSchema,
})
const Last30DaysIntervalSchema = zod.object({
  type: Last30DaysIntervalTypeSchema,
})
const ThisMonthIntervalSchema = zod.object({
  type: ThisMonthIntervalTypeSchema,
})
const LastMonthIntervalSchema = zod.object({
  type: LastMonthIntervalTypeSchema,
})
const CustomIntervalSchema = zod.object({
  type: CustomIntervalTypeSchema,
  start: zod.string().superRefine(DateTimeValidator),
  end: zod.string().superRefine(DateTimeValidator),
})
const WidgetIntervalUnionSchema = zod.discriminatedUnion('type', [
  Last24HoursIntervalSchema, Last7DaysIntervalSchema, Last30DaysIntervalSchema,
  ThisMonthIntervalSchema, LastMonthIntervalSchema, CustomIntervalSchema,
])
const WidgetIntervalCompareToSchema = zod.object({
  compareTo: WidgetIntervalUnionSchema.nullable().default(null),
})
const Last24HoursIntervalWithCompareSchema = Last24HoursIntervalSchema.merge(WidgetIntervalCompareToSchema).extend({
  compareTo: Last24HoursIntervalSchema.nullable().default(null),
})
const Last7DaysIntervalWithCompareSchema = Last7DaysIntervalSchema.merge(WidgetIntervalCompareToSchema).extend({
  compareTo: Last7DaysIntervalSchema.nullable().default(null),
})
const Last30DaysIntervalWithCompareSchema = Last30DaysIntervalSchema.merge(WidgetIntervalCompareToSchema).extend({
  compareTo: Last30DaysIntervalSchema.nullable().default(null),
})
const ThisMonthIntervalWithCompareSchema = ThisMonthIntervalSchema.merge(WidgetIntervalCompareToSchema).extend({
  compareTo: ThisMonthIntervalSchema.nullable().default(null),
})
const LastMonthIntervalWithCompareSchema = LastMonthIntervalSchema.merge(WidgetIntervalCompareToSchema).extend({
  compareTo: LastMonthIntervalSchema.nullable().default(null),
})
const CustomIntervalWithCompareSchema = CustomIntervalSchema.merge(WidgetIntervalCompareToSchema).extend({
  compareTo: CustomIntervalSchema.nullable().default(null),
})
const WidgetIntervalWithCompareUnionSchema = zod.discriminatedUnion('type', [
  Last24HoursIntervalWithCompareSchema, Last7DaysIntervalWithCompareSchema, Last30DaysIntervalWithCompareSchema,
  ThisMonthIntervalWithCompareSchema, LastMonthIntervalWithCompareSchema, CustomIntervalWithCompareSchema,
])
export const WidgetIntervalSchema = WidgetIntervalWithCompareUnionSchema.superRefine((input, ctx) => {
  if (input.type === 'INTERVAL' && DateTime.fromISO(input.start) > DateTime.fromISO(input.end)) {
    ctx.addIssue({
      code: 'custom',
      message: '$t(Validation.startDateAfterEndDate)',
      path: ['start'],
    })
  }
  if (input.compareTo !== null && input.compareTo.type === 'INTERVAL' && DateTime.fromISO(input.compareTo.start) > DateTime.fromISO(input.compareTo.end)) {
    ctx.addIssue({
      code: 'custom',
      message: '$t(Validation.startDateAfterEndDate)',
      path: ['compareTo.start'],
    })
  }
})
export type BaseWidgetInterval = zod.infer<typeof BaseWidgetInterval>
export type WidgetInterval = zod.infer<typeof WidgetIntervalSchema>

export const TooltipCompareTypeSchema = zod.union([
  zod.literal('NONE'),
  zod.literal('NUMERIC'),
  zod.literal('PERCENTAGE'),
])
export type TooltipCompareType = zod.infer<typeof TooltipCompareTypeSchema>

export const WidgetSerieTypeSchema = zod.union([
  zod.literal('line'),
  zod.literal('bar'),
])
export type WidgetSerieType = zod.infer<typeof WidgetSerieTypeSchema>
export const WIDGET_SERIE_TYPE: WidgetSerieType[] = ['line', 'bar']

export const WidgetDataPresentationSchema = zod.union([
  zod.literal('ROWS'),
  zod.literal('COLUMNS'),
])
export type WidgetDataPresentation = zod.infer<typeof WidgetDataPresentationSchema>

export const IntervalSupportSchema = zod.object({
  interval: WidgetIntervalSchema,
})
export const IntervalLockSupportSchema = zod.object({
  dashboardIntervalEnabled: zod.boolean().default(false),
})
export type IntervalLockSupport = zod.infer<typeof IntervalLockSupportSchema>
export type IntervalSupport = zod.infer<typeof IntervalSupportSchema>

export const WidgetElementBufferSupportSchema = zod.object({
  pcNumber: zod.number().nullable(),
  useDefaultPcNumber: zod.boolean().default(true),
  buffer: DeviceBufferSchema.nullable(),
})
type FullWidgetElementBufferSupport = zod.infer<typeof WidgetElementBufferSupportSchema>
export type WidgetElementBufferSupport = AtLeast<FullWidgetElementBufferSupport, 'pcNumber' | 'useDefaultPcNumber'>

export const WidgetMarkAreaElementSchema = zod.object({
  label: zod.string().min(1),
  startDate: zod.string().refine(input => DateTime.fromISO(input).isValid, '$t(Validation.invalid_date_format)'),
  endDate: zod.string().refine(input => DateTime.fromISO(input).isValid, '$t(Validation.invalid_date_format)'),
  color: zod.string().default('#FF0000FF'),
}).superRefine((input, ctx) => {
  if (DateTime.fromISO(input.startDate) > DateTime.fromISO(input.endDate)) {
    ctx.addIssue({
      code: 'custom',
      message: '$t(Validation.startDateAfterEndDate)',
      path: ['startDate'],
    })
  }
})
export type WidgetMarkAreaElement = zod.infer<typeof WidgetMarkAreaElementSchema>

export const MarkableWidgetSchema = zod.object({
  markAreaElements: WidgetMarkAreaElementSchema.array(),
})
export type MarkableWidget = zod.infer<typeof MarkableWidgetSchema>

const WidgetElementBaseSchema = zod.object({
  type: WidgetTypeSchema,
})
export const WidgetElementSchema = WidgetElementBaseSchema.extend({
  id: zod.string(),
  order: zod.number(),
  name: zod.string().min(1).nullable(),
  hexacode: zod.string(),
  department: zod.number(),
  subDepartment: zod.number(),
  pageNumber: zod.number(),
  lineNumber: zod.number(),
  type: WidgetTypeSchema,
})
type WidgetElementBase = zod.infer<typeof WidgetElementBaseSchema>
export type WidgetElement = zod.infer<typeof WidgetElementSchema>
export type PartialWidgetElement<T extends WidgetElementBase> = PartialDeep<T, { recurseIntoArrays: true }>
export type WidgetElementConstructor<T extends WidgetElementBase> = NoArgsConstructor<T>

const WidgetBaseSchema = zod.object({
  type: WidgetTypeSchema,
  elements: WidgetElementBaseSchema.array(),
})
export const WidgetSchema = WidgetBaseSchema.extend({
  id: zod.string(),
  name: zod.string().trim().min(1),
  type: WidgetTypeSchema,
  position: WidgetPositionSchema,
  elements: WidgetElementSchema.array(),
})
type WidgetBase = zod.infer<typeof WidgetBaseSchema>
export type Widget = zod.infer<typeof WidgetSchema>
export type PartialWidget<T extends WidgetBase> = PartialDeep<T, { recurseIntoArrays: true }>
export type WidgetConstructor<T extends WidgetBase> = NoArgsConstructor<T>

export const COMMON_WIDGET_DATA_CONSTRUCTOR = () => ({
  id: crypto.randomUUID(),
  position: {
    i: '',
    x: 0,
    y: 0,
    w: 3,
    h: 3,
  },
})

export const COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR = () => ({
  id: crypto.randomUUID(),
  order: 0,
  name: null,
})

export function defaultInterval(): WidgetInterval {
  return {
    type: 'LAST_24_HOURS',
    compareTo: null,
  }
}
